/**
 * Per-member, per-device flags for the getting-started checklist.
 * localStorage, like celebrationSeen.ts: a server column would need a
 * profiles grant change (20260911190000) + pgTAP, and the worst case of a
 * second device is one more tap. Every storage touch is guarded; the
 * in-memory copy keeps the flags working in private mode.
 */
export type ChecklistFlag = "emailOnlyAlerts" | "visitedMyWork" | "completedSeen";
type Flags = Record<ChecklistFlag, boolean>;

const PREFIX = "wexare-getting-started:";
const EMPTY: Flags = { emailOnlyAlerts: false, visitedMyWork: false, completedSeen: false };
const memory = new Map<string, Flags>();
const listeners = new Set<() => void>();

export function readFlags(userId: string): Flags {
  const cached = memory.get(userId);
  if (cached) return cached;
  let flags: Flags = EMPTY;
  try {
    const raw = localStorage.getItem(PREFIX + userId);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Flags>;
      flags = {
        emailOnlyAlerts: parsed.emailOnlyAlerts === true,
        visitedMyWork: parsed.visitedMyWork === true,
        completedSeen: parsed.completedSeen === true,
      };
    }
  } catch {
    // unreadable storage: start empty
  }
  memory.set(userId, flags);
  return flags;
}

export function setFlag(userId: string, flag: ChecklistFlag): void {
  const current = readFlags(userId);
  if (current[flag]) return;
  const next = { ...current, [flag]: true };
  memory.set(userId, next);
  try {
    localStorage.setItem(PREFIX + userId, JSON.stringify(next));
  } catch {
    // private mode / quota: the in-memory copy carries this session
  }
  listeners.forEach((l) => l());
}

export function subscribeFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function __resetChecklistStorageForTests(): void {
  memory.clear();
  listeners.clear();
}
